import { useState } from "react";
import { toast } from "sonner";
import { Smartphone, X } from "lucide-react";
import { api, idr, pesanGalat } from "../../lib/api";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";

/**
 * Isi/ubah info SATU unit dari Price List — brief owner 22 Sep 2026 G.3 + G1/G2:
 * Serial Number, Battery Health (%), garansi (tanggal berakhir + keterangan
 * singkat), dan harga jual unit (kosong = harga SKU).
 *
 * Hanya dibuka bila server bilang `bisa_isi_info_unit` (kunci
 * `inventory_info_unit.update`, sama dengan `PATCH /inventory/units/{id}/info`).
 * Yang dikirim HANYA medan yang berubah: dua orang yang mengisi BH dan garansi
 * unit yang sama tidak saling menimpa. Kosong = dikosongkan (null).
 */
const inp = "h-9 px-3 border border-slate-200 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-200";

function awal(unit) {
  return {
    serial_number: unit.serial_number || "",
    battery_health: unit.battery_health ?? "",
    garansi_sampai: unit.garansi_sampai || "",
    garansi_keterangan: unit.garansi_keterangan || "",
    harga_jual_unit: unit.harga_jual_unit ?? "",
  };
}

export default function InfoUnitModal({ unit, produk, onClose, onTersimpan }) {
  const [f, setF] = useState(() => awal(unit));
  const [busy, setBusy] = useState(false);
  const bawaan = awal(unit);
  const berSn = (unit.identifier_type || "imei") === "sn";

  const simpan = async () => {
    const kirim = {};
    for (const k of Object.keys(f)) {
      if (String(f[k]).trim() === String(bawaan[k]).trim()) continue;
      const v = String(f[k]).trim();
      if (k === "battery_health" || k === "harga_jual_unit") kirim[k] = v === "" ? null : Number(v);
      else kirim[k] = v === "" ? null : v;
    }
    if (!Object.keys(kirim).length) { onClose(); return; }
    if (kirim.battery_health != null && !(kirim.battery_health >= 0 && kirim.battery_health <= 100
        && Number.isInteger(kirim.battery_health))) {
      toast.error("Battery Health harus bilangan bulat 0–100");
      return;
    }
    setBusy(true);
    try {
      const r = await api.patch(`/inventory/units/${unit.id}/info`, kirim);
      toast.success(r.data?.message || "Info unit disimpan");
      (r.data?.peringatan || []).forEach(p => toast.warning(p));
      onTersimpan?.();
      onClose();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan info unit"));
    } finally {
      setBusy(false);
    }
  };

  const guard = useModalGuard({ onClose, onSubmit: simpan, busy, dangerous: true });
  const ubah = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}
         data-testid="pl-info-unit-modal">
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()}
           className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-emerald-600" /> Info Unit
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{produk?.nama}</p>
            <p className="text-xs font-mono text-violet-700 mt-0.5">
              {berSn ? "SN" : "IMEI"} {unit.identifier}
            </p>
          </div>
          <button type="button" onClick={guard.requestClose} className="text-slate-400 hover:text-slate-700"
                  aria-label="Tutup" data-testid="pl-info-tutup">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          {berSn ? (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              Produk ini ber-SN — nomornya sudah tercatat sebagai identitas unit.
            </p>
          ) : (
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Serial Number</span>
              <input value={f.serial_number} onChange={ubah("serial_number")} maxLength={64}
                     data-testid="pl-info-sn" placeholder="mis. G17TCXRKF0" className={`${inp} font-mono`} />
            </label>
          )}
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Battery Health (%)</span>
            <input type="number" min={0} max={100} step={1} value={f.battery_health}
                   onChange={ubah("battery_health")} data-testid="pl-info-bh" placeholder="mis. 87"
                   className={inp} />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Garansi sampai</span>
              <input type="date" value={f.garansi_sampai} onChange={ubah("garansi_sampai")}
                     data-testid="pl-info-garansi" className={inp} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Keterangan garansi</span>
              <input value={f.garansi_keterangan} onChange={ubah("garansi_keterangan")} maxLength={80}
                     data-testid="pl-info-garansi-ket" placeholder="mis. Resmi iBox" className={inp} />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Harga jual unit ini (Rp)</span>
            <input type="number" min={0} step={1000} value={f.harga_jual_unit}
                   onChange={ubah("harga_jual_unit")} data-testid="pl-info-harga"
                   placeholder={`kosong = harga produk ${idr(produk?.harga_jual || 0)}`} className={inp} />
            <span className="block text-[11px] text-slate-400 mt-1">
              Jadi harga bawaan saat IMEI ini dipilih di nota — kasir tetap bisa mengubahnya.
            </span>
          </label>
        </div>

        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} />
        )}
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={guard.requestClose} disabled={busy}
                  className="h-9 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            Batal
          </button>
          <button type="button" onClick={simpan} disabled={busy} data-testid="pl-info-simpan"
                  className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60">
            {busy ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}
